<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, string } from 'yup'
import { useI18n } from 'vue-i18n'

import {
  DCard,
  DBtn,
  DSelectFieldSearch,
  DMultiselect,
  DModal,
  DEmployeesFilter,
  DLoading,
  DTextField,
  FilterLinesIcon,
  Users01Icon
} from 'front-library'
import {
  convertArrayToString,
  getPayroll,
  getReportRequest,
  getEmployeesReportByRecordId,
  getEmployerRecords,
  getGovernmentEmployees
} from '@/app/core/services/reports'
import { ReportSUALicenses, generateSUALicenses } from '@/app/core/services/reports'

usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
  (items = items.filter(
    (item: any) =>
      item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
  ))
})

/* Datos  */
const itemsPayrolls = ref<any[]>([])
const itemsEmployerRecords = ref<any[]>([])
const itemsEmployees = ref<string[]>([])
const urlFile = ref(null)
const refresh = ref()
const path = route.params.code as string
const decodeCode = atob(path)

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  employerRecordId: string().required(t('form.valid.required')),
  employees: array()
    .min(1, t('form.valid.employeeSelectionRequired'))
    .required(t('form.valid.fieldRequired', { field: t('fields.employees') })),
  startDate: string().required(t('form.valid.required')),
  endDate: string().required(t('form.valid.required'))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  initialValues: {},
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>('payrolls')
const { value: employerRecordId } = useField<string>('employerRecordId')
const { value: employees } = useField<any[]>('employees')
const { value: startDate } = useField<string>('startDate')
const { value: endDate } = useField<string>('endDate')
const { value: format } = useField('format')

const onSubmit = handleSubmit((values: any) => {
  const createReport: ReportSUALicenses = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateSUALicenses(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

const reportRequest = (id: string) => {
  urlFile.value = null

  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

const getListProcessedPayroll = async () => {
  itemsEmployees.value = []
  employees.value = []
  try {
    if (
      payrolls.value?.length > 0 &&
      startDate.value &&
      endDate.value
    ) {
      loading.value = true
      messageLoading.value = t('loading.employee')
      const strPayrolls = await convertArrayToString(payrolls.value)
      const employeesGovernmentIds = await getGovernmentEmployees(
        strPayrolls,
        startDate.value,
        endDate.value,
        ['licenses']
      )
      if (employeesGovernmentIds.length === 0) return
      
      const strEmployees = await convertArrayToString(employeesGovernmentIds)
      itemsEmployees.value = await getEmployeesReportByRecordId(strEmployees, employerRecordId.value)
      employees.value = itemsEmployees.value.map((_: any) => _.id)
      loading.value = false
    }
  } catch (error: any) {
    showError(error)
  }
}

const getListEmployees = async () => {
  loading.value = true
  messageLoading.value = t('loading.employee')
  try {
    if (payrolls.value.length !== 0 &&
      startDate.value &&
      endDate.value &&
      employerRecordId.value) {
      await getListProcessedPayroll()
    } else {
      notifications.addNotification({
        title: t('reports.notification.noEmployeesFound'),
        text: t('reports.notification.noEmployees'),
        type: 'warning',
        time: 10000
      })
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

const getListEmployerRecords = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const companyId = storePayroll.payroll?.company.id
    if (companyId) {
      itemsEmployerRecords.value = await getEmployerRecords(companyId)
      if (itemsEmployerRecords.value.length === 0) {
        notifications.addNotification({
          title: t('reports.notification.noEmployerRecordsFound'),
          text: t('reports.notification.noEmployerRecords'),
          type: 'warning',
          time: 10000
        })
      }
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

const closeModal = () => {
  showModal.value = false
}

const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const promisePayrolls = await getPayroll(store.user?.id as string)
    itemsPayrolls.value = promisePayrolls
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)

    await Promise.all([
      getListEmployees(),
      getListEmployerRecords()
    ])
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>

<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />

    <DModal :open="showModal" :typeAlert="typeAlertModal" :title="titleModal" :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a class="underline tracking-widest text-blue-600" :href="urlFile" target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>

    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p class="text-3xl font-semibold text-shadow text-blue-900">
          {{ t('reports.suaLicenses') }}
        </p>
      </div>
    </div>

    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-3 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect :label="t('reports.fields.payrolls')" v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')" :required="true" :options="itemsPayrolls"
                    :error="errors.payrolls" @change="getListEmployees()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch :label="t('reports.fields.employerRecord')" v-model="employerRecordId"
                    :required="true" :options="itemsEmployerRecords" :error="errors.employerRecordId"
                    @change="getListEmployees()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField type="date" :label="t('reports.fields.startDate')" v-model="startDate" :required="true"
                    :error="errors.startDate" @change="getListEmployees()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField type="date" :label="t('reports.fields.endDate')" v-model="endDate" :required="true"
                    :error="errors.endDate" @change="getListEmployees()" />
                </div>

              </div>
            </div>

            <div class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="pl-4 pr-4 lg:pr-0 flex justify-between">
                <h3 class="text-md font-medium leading-6 text-gray-900">
                  {{ t('fields.employees') }}
                </h3>
                <h3 class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter :items="itemsEmployees" v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div class="flex justify-center items-center p-5 sm:gap-4 lg:gap-3 xl:gap-4">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{ `${employees?.length || 0}/${itemsEmployees?.length || 0}` }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>

          <!-- Footer Buttons -->
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn type="button" color="secondary" @click="back" class="px-5 py-2 font-semibold">
                {{ t('form.button.back') }}
              </DBtn>
              <DBtn class="px-5 py-2 font-semibold" @click.prevent="onSubmit(), format = 'text/plain'">
                {{ t('form.button.file') }}
              </DBtn>
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>